/* eslint-disable import/prefer-default-export */
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

let fontsLoaded = false;

const FONTS_TO_LOAD = [
    { label: 'Regular', vfsName: 'NotoSansTC-Regular.woff' },
    { label: 'Bold', vfsName: 'NotoSansTC-Bold.woff' },
];

function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    const CHUNK = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
    }
    return btoa(binary);
}

async function fetchFontWithProgress(url, onChunk) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to load font ${url}: ${res.status}`);
    const total = +(res.headers.get('Content-Length') || 0);
    const reader = res.body.getReader();
    const chunks = [];
    let received = 0;
    for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        if (onChunk) onChunk(received, total);
    }
    const merged = new Uint8Array(received);
    let offset = 0;
    chunks.forEach((chunk) => {
        merged.set(chunk, offset);
        offset += chunk.length;
    });
    return merged.buffer;
}

export function isNotoSansTCFontsLoaded() {
    return fontsLoaded;
}

export async function ensureNotoSansTCFontsLoaded(onProgress) {
    if (fontsLoaded) return;

    if (!pdfMake.vfs) pdfMake.vfs = pdfFonts.pdfMake.vfs;

    const baseUrl = window.location.origin;

    for (let i = 0; i < FONTS_TO_LOAD.length; i += 1) {
        const { label, vfsName } = FONTS_TO_LOAD[i];
        if (pdfMake.vfs[vfsName]) continue; // eslint-disable-line no-continue

        // eslint-disable-next-line no-await-in-loop
        const buffer = await fetchFontWithProgress(
            `${baseUrl}/fonts/${vfsName}`,
            (received, contentLength) => {
                if (onProgress) {
                    onProgress({
                        index: i,
                        total: FONTS_TO_LOAD.length,
                        label,
                        received,
                        contentLength,
                    });
                }
            },
        );
        pdfMake.vfs[vfsName] = arrayBufferToBase64(buffer);
    }

    pdfMake.fonts = {
        NotoSansTC: {
            normal: 'NotoSansTC-Regular.woff',
            bold: 'NotoSansTC-Bold.woff',
        },
    };

    fontsLoaded = true;
}

export function startEstimatedProgressTicker({ estimatedMs = 5000, onTick, cap = 0.99 }) {
    const start = performance.now();
    let cancelled = false;
    let externalPct = 0;
    let raf = null;

    const tick = () => {
        if (cancelled) return;
        const elapsed = performance.now() - start;
        const timePct = Math.min(elapsed / estimatedMs, cap);
        const pct = Math.min(Math.max(timePct, externalPct), cap);
        onTick(pct);
        raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return {
        setExternal: (p) => { externalPct = p; },
        stop: () => {
            cancelled = true;
            if (raf != null) cancelAnimationFrame(raf);
        },
    };
}

export async function createPdfBlobByPdfMakerDefinition(docDefinition, onFontProgress) {
    await ensureNotoSansTCFontsLoaded(onFontProgress);

    return new Promise((resolve) => {
        pdfMake.createPdf(docDefinition).getBlob((blob) => {
            resolve(blob);
        });
    });
}
